import React, { useEffect, useState } from "react";
import { useReviewStore } from "../../store/reviewStore";
import Loader from "../loader/Loader";
import profileImg from "../../assets/profile.png"; // ваш аватар
import Star from "../../assets/icons/Star";

interface NewReview {
  stars: number;
  content: string;
  author_name: string;
  phone: string;
}

const ReviewSection: React.FC<{ productId: number }> = ({ productId }) => {
  const { reviews, fetchReviews, addReview, loading } = useReviewStore();
  
  // Состояние для новой формы отзыва
  const [newReview, setNewReview] = useState<NewReview>({
    stars: 5,
    content: "",
    author_name: "",
    phone: "",
  });

  // Состояние для отображения "Спасибо за ваш отзыв"
  const [reviewSubmitted, setReviewSubmitted] = useState(false);

  // Загружаем отзывы при монтировании/смене productId
  useEffect(() => {
    fetchReviews(productId);
  }, [productId]);

  // Фильтруем только approved
  const approvedReviews = reviews.filter((r) => r.status === "approved");

  // Отправка отзыва
  const handleSubmit = async () => {
    try {
      await addReview(productId, newReview);
      // Сбрасываем форму
      setNewReview({
        stars: 5,
        content: "",
        author_name: "",
        phone: "",
      });
      // Показываем сообщение "Спасибо за ваш отзыв!"
      setReviewSubmitted(true);
    } catch (error) {
      console.error("Ошибка при добавлении отзыва:", error);
      // Можно добавить показ ошибки пользователю
    }
  };

  // Функция для форматирования даты (пример)
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("ru-RU");
  };

  return (
    <div className="flex flex-col items-center mt-12 w-full">
      <h2 className="text-xl font-bold mb-6">Отзывы наших клиентов</h2>

      {/* Лоадер */}
      {loading && <Loader />}

      {reviewSubmitted ? (
        <div className="text-center text-black/50">
          <p className="font-semibold">Спасибо за ваш отзыв!</p>
          <p>В скором времени он отобразится на странице данного продукта</p>
        </div>
      ) :
      !loading && approvedReviews.length === 0 && (
        <p className="text-black/50">
          Оставьте первый отзыв — это поможет другим принять решение.
        </p>
      )}

      {/* Сетка карточек отзывов */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 w-full">
        {approvedReviews.map((review) => (
          <div
            key={review.id}
            className="bg-white p-4 border rounded flex flex-col"
          >
            {/* Аватар + Имя + Дата */}
            <div className="flex items-center gap-3 mb-3">
              <img
                src={profileImg}
                alt="avatar"
                className="w-10 h-10 rounded-full object-cover"
              />
              <div>
                <p className="font-bold text-black">{review.author_name}</p>
                <p className="text-sm text-gray-500">
                  {formatDate(review.created_at)}
                </p>
              </div>
            </div>

            {/* Рейтинг (просто заполненные звёзды, без клика) */}
            <div className="flex items-center mb-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star key={i} filled={i < review.stars} 
                size={15}           // уменьшаем звёзды до 20px
                strokeWidth={2}     // при необходимости уменьшаем толщину обводки
              />
              ))}
            </div>

            {/* Текст отзыва */}
            <p className="text-black">{review.content}</p>
          </div>
        ))}
      </div>

      {/* Форма добавления отзыва */}
      {!reviewSubmitted && (
        <div className="flex flex-col items-center mt-12 w-full max-w-lg">
        <div className="flex gap-2 mb-4">
          {[...Array(5)].map((_, index) => (
            <Star
              key={index}
              filled={newReview.stars > index}
              onClick={() =>
                setNewReview((prev) => ({ ...prev, stars: index + 1 }))
              }
            />
          ))}
        </div>
        <span className="text-black mb-6">Поставьте оценку товару</span>

        <div className="w-full">
          <label className="block text-black mb-1">Комментарий</label>
          <textarea
            className="w-full border rounded p-2 mb-4 resize-none h-24"
            placeholder="Общее впечатление"
            value={newReview.content}
            onChange={(e) =>
              setNewReview((prev) => ({ ...prev, content: e.target.value }))
            }
          />

          <div className="flex gap-4">
            <input
              className="w-1/2 border rounded p-2"
              type="text"
              placeholder="Имя"
              value={newReview.author_name}
              onChange={(e) =>
                setNewReview((prev) => ({
                  ...prev,
                  author_name: e.target.value,
                }))
              }
            />
            <input
              className="w-1/2 border rounded p-2"
              type="tel"
              placeholder="Номер телефона"
              value={newReview.phone}
              onChange={(e) =>
                setNewReview((prev) => ({ ...prev, phone: e.target.value }))
              }
            />
          </div>
        </div>

        <button
          onClick={handleSubmit}
          className="mt-6 px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-800"
        >
          Оставить отзыв
        </button>
      </div>
      )}
    </div>
  );
};

export default ReviewSection;
